import menuArrowIcon from "@assets/images/menu-arrow-icon.svg";
import trashIcon from "@assets/images/Trash.svg";
import { Grid, TextField } from "@material-ui/core";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import MultiChipsSelectField from "@saleor/components/MultiChipsSelectField";
import React, { ChangeEvent, FC, useCallback, useState } from "react";
import Svg from "react-inlinesvg";
import { useIntl } from "react-intl";

import { IUnlockCode, IUnlockCodeErrorDetail } from "../../useUnlockCodesState";
import { generateCardBorderColor } from "../../utils";
import CollapsedCard from "../CollapsedCard/CollapsedCard";
import DeleteWarnDialog from "../DeleteWarnDialog/DeleteWarnDialog";
import useStyles from "./styles";

interface IUnlockCodeCardProps {
  unlockCodeData: IUnlockCode;
  onModifyUnlockCodeData: (modifiedUnlockCodeData: IUnlockCode) => void;
  unlockCodeErrors: IUnlockCodeErrorDetail;
  hasDeleteButton?: boolean;
  onDelete?: () => void;
  isDisabled: boolean;
}

type TModifiableFields =
  | "unlockCode"
  | "unlockCodeUsageLimit"
  | "couponReportTags"
  | "description";

const UnlockCodeCard: FC<IUnlockCodeCardProps> = ({
  unlockCodeData,
  onModifyUnlockCodeData,
  unlockCodeErrors,
  hasDeleteButton = false,
  onDelete = () => ({}),
  isDisabled
}) => {
  const intl = useIntl();
  const classes = useStyles({
    cardBorderLeftColor: generateCardBorderColor(
      unlockCodeData,
      unlockCodeErrors
    )
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDeleteDialogShown, setIsDeleteDialogShown] = useState(false);
  const [couponReportTagText, setCouponReportTagText] = useState("");

  const handleCouponReportTagTextChange = useCallback((tagText: string) => {
    setCouponReportTagText(tagText);
  }, []);

  const handleToggleDeleteDialogShown = useCallback(
    (isShown: boolean) => () => {
      if (isShown && !isDisabled) {
        if (
          !!unlockCodeData.unlockCode ||
          !!unlockCodeData.couponReportTags.length ||
          !!unlockCodeData.description
        ) {
          // if has already inputted something, show warn dialog
          setIsDeleteDialogShown(true);
        } else {
          // else delete immediately
          onDelete();
        }
      } else {
        setIsDeleteDialogShown(false);
      }
    },
    [setIsDeleteDialogShown, unlockCodeData, isDisabled]
  );

  const handleDelete = useCallback(() => {
    onDelete();
  }, [setIsDeleteDialogShown, onDelete]);

  const handleNumberOnKeyDown = event => {
    if (event.key === "." || event.key === "-") {
      event.preventDefault();
    }
  };

  const handleModifyUnlockCodeData = useCallback(
    (modifiedField: TModifiableFields) => (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (modifiedField === "unlockCode") {
        onModifyUnlockCodeData({
          ...unlockCodeData,
          [modifiedField]: event.target.value.replace(/\s/g, "").toUpperCase() // prevent spaces
        });
      } else {
        onModifyUnlockCodeData({
          ...unlockCodeData,
          [modifiedField]: event.target.value
        });
      }
    },
    [onModifyUnlockCodeData, unlockCodeData]
  );

  const handleCodeUsageLimitChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (/^\d{1,9}$/.test(event.target.value)) {
        onModifyUnlockCodeData({
          ...unlockCodeData,
          unlockCodeUsageLimit: parseInt(event.target.value, 10)
        });
      } else if (event.target.value === "") {
        onModifyUnlockCodeData({
          ...unlockCodeData,
          unlockCodeUsageLimit: 0
        });
      }
    },
    [onModifyUnlockCodeData, unlockCodeData]
  );

  const handleCouponReportTagsChange = useCallback(
    (mode: "add" | "delete", couponReportTag: string) => {
      if (
        mode === "add" &&
        !unlockCodeData.couponReportTags.some(
          currTag => currTag === couponReportTag
        )
      ) {
        onModifyUnlockCodeData({
          ...unlockCodeData,
          couponReportTags: [
            ...unlockCodeData.couponReportTags,
            couponReportTag
          ]
        });
      } else if (mode === "delete") {
        onModifyUnlockCodeData({
          ...unlockCodeData,
          couponReportTags: unlockCodeData.couponReportTags.filter(
            currTag => currTag !== couponReportTag
          )
        });
      }
    },
    [onModifyUnlockCodeData, unlockCodeData]
  );

  const handleSetCollapsed = useCallback(
    (isCollapsedVal: boolean) => () => {
      setIsCollapsed(isCollapsedVal);
    },
    [setIsCollapsed]
  );

  if (isCollapsed) {
    return (
      <CollapsedCard
        unlockCodeData={unlockCodeData}
        unlockCodeErrors={unlockCodeErrors}
        onExpand={handleSetCollapsed(false)}
      />
    );
  }

  return (
    <>
      <div className={classes.expandedCardContainer}>
        <div className={classes.expandedCardContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={intl.formatMessage({
                  defaultMessage: "Unlock Code"
                })}
                name="unlockCode"
                fullWidth
                id={`unlockCode${unlockCodeData.componentKey}-unlockCodeTxt`}
                value={unlockCodeData.unlockCode}
                onChange={handleModifyUnlockCodeData("unlockCode")}
                inputProps={{
                  maxLength: 50
                }}
                error={!!unlockCodeErrors?.unlockCode}
                FormHelperTextProps={{
                  className: classes.formHelperTextProps
                }}
                disabled={isDisabled}
                helperText={
                  <>
                    <div className={classes.infoWithCharCountContainer}>
                      <div className={classes.errorText}>
                        {unlockCodeErrors?.unlockCode ?? ""}
                      </div>
                      <div>{unlockCodeData.unlockCode.length}/50</div>
                    </div>
                  </>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={intl.formatMessage({
                  defaultMessage: "Unlock Code Usage Limit"
                })}
                name="unlockCodeUsageLimit"
                type="number"
                value={unlockCodeData.unlockCodeUsageLimit.toString()}
                onChange={handleCodeUsageLimitChange}
                fullWidth
                id={`unlockCode${unlockCodeData.componentKey}-unlockCodeUsageLimitTxt`}
                onKeyDown={handleNumberOnKeyDown}
                FormHelperTextProps={{
                  className: classes.formHelperTextProps
                }}
                disabled={isDisabled}
                helperText={
                  <>
                    <div className={classes.helperTextContainer}>
                      <InfoOutlined
                        fontSize="small"
                        style={{
                          position: "relative",
                          top: 1
                        }}
                      />
                      '0' means that the coupon can be used on unlimited number
                      of times.
                    </div>
                  </>
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              className={classes.largerTextFieldContainer}
            >
              <MultiChipsSelectField
                name="couponReportTags"
                label={intl.formatMessage({
                  defaultMessage: "Coupon Report Tags"
                })}
                choices={[]}
                onKeyPress={() => ({})}
                hasMore={false}
                loading={false}
                value={unlockCodeData.couponReportTags}
                onChange={evt =>
                  handleCouponReportTagsChange("add", evt.target.value)
                }
                onRemove={evt =>
                  handleCouponReportTagsChange("delete", evt.target.value)
                }
                add={{
                  label: couponReportTagText,
                  onClick: () =>
                    handleCouponReportTagsChange("add", couponReportTagText)
                }}
                onChangeText={handleCouponReportTagTextChange}
                displayValues={unlockCodeData.couponReportTags.map(tagStr => ({
                  label: tagStr,
                  value: tagStr
                }))}
                formHelperTextProps={{
                  className: classes.formHelperTextProps
                }}
                error={!!unlockCodeErrors?.couponReportTags}
                disabled={isDisabled}
                helperText={
                  <>
                    <div className={classes.infoWithCharCountContainer}>
                      <div className={classes.helperTextContainer}>
                        <InfoOutlined
                          fontSize="small"
                          style={{
                            position: "relative",
                            top: 1
                          }}
                        />
                        This will serve as a reference for reports.
                      </div>
                    </div>
                    <div>{unlockCodeErrors?.couponReportTags ?? ""}</div>
                  </>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                className={classes.largerTextFieldContainer}
                label={intl.formatMessage({
                  defaultMessage: "Description"
                })}
                placeholder="Tell something about this unlock code."
                name="description"
                id={`unlockCode${unlockCodeData.componentKey}-descriptionTxt`}
                value={unlockCodeData.description}
                onChange={handleModifyUnlockCodeData("description")}
                error={!!unlockCodeErrors?.description}
                fullWidth
                multiline
                rows={2}
                inputProps={{
                  maxLength: 500
                }}
                FormHelperTextProps={{
                  className: classes.formHelperTextProps
                }}
                disabled={isDisabled}
                helperText={
                  <>
                    <div className={classes.infoWithCharCountContainer}>
                      <div className={classes.helperTextContainer}>
                        <InfoOutlined
                          fontSize="small"
                          style={{
                            position: "relative",
                            top: 1
                          }}
                        />
                        It should be a descriptive keyword or phrase related to
                        the unlock code.
                      </div>
                      <div>{unlockCodeData.description.length}/500</div>
                    </div>
                    <div>{unlockCodeErrors?.description ?? ""}</div>
                  </>
                }
              />
            </Grid>
          </Grid>
        </div>
        <div className={classes.expandedCardFooter}>
          {hasDeleteButton ? (
            <div
              id={`unlockCode${unlockCodeData.componentKey}-expandedDeleteIcon`}
              onClick={handleToggleDeleteDialogShown(true)}
              className={classes.trashIconContainer}
            >
              <Svg src={trashIcon} />
            </div>
          ) : (
            <div />
          )}
          <div
            id={`unlockCode${unlockCodeData.componentKey}-expandedArrowIcon`}
            onClick={handleSetCollapsed(true)}
            className={classes.collapseArrowContainer}
          >
            <Svg src={menuArrowIcon} />
          </div>
        </div>
      </div>
      <DeleteWarnDialog
        isDialogShown={isDeleteDialogShown}
        onClose={handleToggleDeleteDialogShown(false)}
        onDelete={handleDelete}
      />
    </>
  );
};

export default UnlockCodeCard;
